import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { selectIsAccountAddressReverted } from '@epicuro-next/platform/configuration';
import {
  confirmActiveAccountSelection,
  loadAccounts,
  selectAccountsIsLoading,
  selectAccountsQuerySearch,
  selectAllAccountsLoaded,
  selectCurrentAccountList,
  selectNoSearchResult,
  setPaginationQuery,
} from '@epicuro-next/state/accounts-state';
import {
  selectIsNoBasket,
  setActiveBasketAccounts,
  updateBasketShops,
} from '@epicuro-next/state/basket-state';
import { preloadSites } from '@epicuro-next/state/site-state';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { sortAccountsBySelection } from '../../shared/sort-accounts-by-selection/sort-accounts-by-selection';
import { AccountsSelectorComponentStore } from '../../store/accounts-selector.component-store';
import { SelectionAccountPayload, SelectionAddressPayload } from '../../types';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccountSelectorBaseComponent implements OnInit, OnDestroy {
  public destroy$ = new Subject<void>();

  public accounts$ = this.store.pipe(select(selectCurrentAccountList));

  public isAccountAddressReverted$ = this.store.pipe(
    select(selectIsAccountAddressReverted),
  );

  public isLoading$: Observable<boolean> = this.store.pipe(
    select(selectAccountsIsLoading),
  );

  public allAccountsLoaded$: Observable<boolean> = this.store.pipe(
    select(selectAllAccountsLoaded),
  );

  public noSearchResult$: Observable<boolean> = this.store.pipe(
    select(selectNoSearchResult),
  );

  public accountSearchValue$ = this.store.pipe(
    select(selectAccountsQuerySearch),
  );

  public sortedAccounts$ = this.accounts$.pipe(
    withLatestFrom(
      this.componentStore.appliedActiveAccountId$,
      this.componentStore.selectedAccountIds$,
    ),
    map(([accounts, activeAccountId, selectedAccountIds]) => {
      return sortAccountsBySelection(
        accounts,
        selectedAccountIds,
        activeAccountId as string,
      );
    }),
  );

  public isSingleSelection$ = this.componentStore.isSingleSelection$;
  public shouldHighlightAddress$ = this.componentStore.shouldHighlightAddress$;
  public searchCleared$ = this.componentStore.searchCleared$;
  public selectedAccountIds$ = this.componentStore.selectedAccountIds$;
  public selectedAddressesIds$ = this.componentStore.selectedAddressesIds$;
  public selectedShopsCount$ = this.componentStore.selectedShopsCount$;
  public isConfirmButtonDisabled$ = this.componentStore.isConfirmDisabled$;
  public initialSelectedAccountIds$ =
    this.componentStore.initialSelectedAccountIds$;
  public isMovingBasket$ = this.componentStore.isMovingBasketInProgress$;
  public closeModal$ = this.componentStore.closeModal$;

  constructor(
    protected store: Store,
    protected componentStore: AccountsSelectorComponentStore,
  ) {}

  public ngOnInit(): void {
    this.allAccountsLoaded$
      .pipe(
        take(1),
        filter((loaded) => !loaded),
      )
      .subscribe(() => this.store.dispatch(loadAccounts()));

    this.selectedAccountIds$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((selectedIds) => {
        selectedIds.forEach((accountId) => {
          this.store.dispatch(preloadSites({ accountId }));
        });
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public updateBasket(): void {
    combineLatest([
      this.store.pipe(select(selectIsNoBasket)),
      this.componentStore.appliedBasketAccounts$,
      this.componentStore.appliedActiveAccount$,
    ])
      .pipe(take(1))
      .subscribe(([isNoBasket, selectedAccounts, activeAccount]) => {
        if (isNoBasket) {
          this.store.dispatch(
            setActiveBasketAccounts({ basketAccounts: selectedAccounts }),
          );
          this.store.dispatch(confirmActiveAccountSelection({ activeAccount }));
        } else {
          this.store.dispatch(
            updateBasketShops({
              accounts: selectedAccounts,
            }),
          );
        }
      });
  }

  public confirm(): void {
    combineLatest([
      this.componentStore.appliedBasketAccounts$,
      this.componentStore.appliedActiveAccount$,
    ])
      .pipe(
        take(1),
        tap(([selectedAccounts, activeAccount]) => {
          this.store.dispatch(
            setActiveBasketAccounts({ basketAccounts: selectedAccounts }),
          );
          this.store.dispatch(confirmActiveAccountSelection({ activeAccount }));
        }),
      )
      .subscribe();
  }

  public handleAccountToggle(selection: SelectionAccountPayload): void {
    if (selection.isSelected) {
      this.componentStore.selectAccount(
        selection.account,
        selection.isSingleSelection,
      );
    } else {
      this.componentStore.deselectAccount(selection.account);
    }
  }

  public handleAddressToggle(selection: SelectionAddressPayload): void {
    if (selection.isSelected) {
      this.componentStore.selectAddress(
        selection.address,
        selection.isSingleSelection,
      );
    } else {
      this.componentStore.deselectAddress(selection.account, selection.address);
    }
  }

  public handleDeselectAll() {
    this.componentStore.deselectAll();
  }

  public setPage() {
    combineLatest([this.allAccountsLoaded$, this.isLoading$])
      .pipe(
        take(1),
        filter(([allLoaded, isLoading]) => !(allLoaded || isLoading)),
      )
      .subscribe(() => {
        this.store.dispatch(setPaginationQuery());
      });
  }
}
